import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, Check } from "lucide-react";
import { MaskLines, Reveal, Stagger, Item } from "../Motion";
import { Btn, Container, Eyebrow, ArrowLink } from "../Shared";
import { LogoMark } from "../Logo";
import { useLang } from "../../lib/lang";

export const Hero = () => {
  const { t, r } = useLang();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const onMove = (e) => {
    const b = ref.current.getBoundingClientRect();
    mx.set((e.clientX - b.left) / b.width - 0.5);
    my.set((e.clientY - b.top) / b.height - 0.5);
  };
  return (
    <section ref={ref} onMouseMove={onMove} className="relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0 [background-image:linear-gradient(to_right,rgba(18,32,43,0.05)_1px,transparent_1px)] [background-size:calc(100%/6)_100%] md:[background-size:calc(100%/12)_100%]" />
      <Container className="relative grid min-h-[calc(100svh-72px)] items-center gap-12 py-14 lg:grid-cols-12 lg:py-10">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.1 }}><Eyebrow>{t.hero.eyebrow}</Eyebrow></motion.div>
          <h1 className="mt-8 font-display text-[2.7rem] font-light leading-[0.98] tracking-[-0.035em] text-[var(--ink)] sm:text-6xl lg:text-[4.6rem] xl:text-[5.2rem]" data-testid="hero-headline">
            <MaskLines lines={[t.hero.l1, t.hero.l2, t.hero.l3]} lineClass={(i) => (i === 1 ? "italic text-[var(--teal-ink)]" : "")} />
          </h1>
          <Reveal delay={0.6} className="mt-8 max-w-xl text-base text-[var(--muted)] md:text-lg">{t.hero.sub}</Reveal>
          <Reveal delay={0.75} className="mt-10 flex flex-wrap items-center gap-3">
            <Btn to={r("upload")} data-testid="hero-quote-btn">{t.hero.cta1}</Btn>
            <Btn to="#servicios" variant="ghost" icon="down" data-testid="hero-services-btn" onClick={(e) => { e.preventDefault(); document.getElementById("servicios")?.scrollIntoView({ behavior: "smooth" }); }}>{t.hero.cta2}</Btn>
          </Reveal>
          <Reveal delay={0.9} className="mt-10 flex items-center gap-3 text-sm text-[var(--muted)]">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--teal)]/15"><Check className="h-3.5 w-3.5 text-[var(--teal-ink)]" /></span>{t.hero.note}
          </Reveal>
        </motion.div>
        <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.4, delay: 0.3, ease: [0.65, 0, 0.35, 1] }} className="relative mx-auto aspect-[4/5] w-full max-w-[460px] overflow-hidden rounded-sm bg-[var(--ink)]">
            <motion.img style={{ y: imgY, scale: 1.18 }} src="/media/img/a2.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-90 grayscale" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6 text-white" style={{ transform: "translateZ(40px)" }}>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/85">ES · DE · EN</p>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/85">Münster</p>
            </div>
          </motion.div>
          <motion.div style={{ rotateX: rx, rotateY: ry }} className="pointer-events-none absolute -left-6 -top-10 hidden w-56 lg:block" aria-hidden="true">
            <LogoMark className="h-auto w-full opacity-90" draw />
          </motion.div>
        </div>
      </Container>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:block">
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4 }} className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--ink)]/20"><ArrowDown className="h-4 w-4" /></motion.span>
      </motion.div>
    </section>
  );
};

const AudCard = ({ data, to, dark, id, img }) => (
  <Item className={`group relative flex min-h-[520px] flex-col justify-between overflow-hidden rounded-sm p-8 md:p-12 ${dark ? "bg-[var(--ink)] text-white" : "bg-white text-[var(--ink)] border border-[var(--line)]"}`}>
    <img src={img} alt="" loading="lazy" className={`absolute right-0 top-0 h-full w-1/2 object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105 [clip-path:polygon(35%_0,100%_0,100%_100%,0_100%)] ${dark ? "opacity-30" : "opacity-20"} grayscale`} />
    <div className="relative">
      <Eyebrow light={dark}>{data.tag}</Eyebrow>
      <h3 className="mt-6 max-w-md font-display text-3xl font-light leading-tight md:text-[2.6rem]">{data.title}</h3>
      <p className={`mt-5 max-w-md ${dark ? "text-white/75" : "text-[var(--muted)]"}`}>{data.text}</p>
    </div>
    <div className="relative mt-10">
      <ul className="space-y-3">
        {data.items.map((i) => (
          <li key={i} className={`flex items-center gap-3 border-t pt-3 ${dark ? "border-white/15" : "border-[var(--line)]"}`}><span className="h-2 w-2 rotate-45 border border-[var(--teal)]" />{i}</li>
        ))}
      </ul>
      <div className="mt-8"><ArrowLink to={to} light={dark} data-testid={id}>{data.link}</ArrowLink></div>
    </div>
  </Item>
);

export const Audience = () => {
  const { t, r } = useLang();
  return (
    <section className="py-24 md:py-36" data-testid="audience-section">
      <Container>
        <div className="mb-14 grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-4"><Eyebrow>{t.aud.eyebrow}</Eyebrow></Reveal>
          <h2 className="font-display text-3xl font-light leading-tight tracking-tight text-[var(--ink)] md:text-5xl lg:col-span-8"><MaskLines inView lines={[t.aud.title]} /></h2>
        </div>
        <Stagger className="grid gap-6 lg:grid-cols-2">
          <AudCard data={t.aud.private} to={r("certified")} id="audience-private-link" img="/media/img/a3.jpg" />
          <AudCard data={t.aud.business} to={r("interpreting")} dark id="audience-business-link" img="/media/img/a1.jpg" />
        </Stagger>
      </Container>
    </section>
  );
};

const BentoCard = ({ k, to, className, variant = "light", img, n }) => {
  const { t } = useLang();
  const [title, desc] = t.services.cards[k];
  const styles = { light: "bg-white border border-[var(--line)] text-[var(--ink)]", dark: "bg-[var(--ink)] text-white", teal: "bg-[#DCEFEC] text-[var(--ink)]", sand: "bg-[var(--paper-2)] text-[var(--ink)]" }[variant];
  return (
    <Item className={`${className}`}>
      <Link to={to} className={`group relative flex h-full min-h-[240px] flex-col justify-between overflow-hidden rounded-sm p-7 md:p-9 transition-transform duration-500 hover:-translate-y-1 ${styles}`} data-testid={`service-card-${k}`}>
        {img && <img src={img} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25 grayscale transition-[transform,opacity] duration-[1.2s] group-hover:scale-105 group-hover:opacity-35" />}
        <div className="relative flex items-start justify-between gap-4">
          <span className={`font-mono text-xs ${variant === "dark" ? "text-white/70" : "text-[var(--muted)]"}`}>{n}</span>
          <span className={`flex h-10 w-10 items-center justify-center rounded-full border transition-[background-color,transform] duration-500 group-hover:rotate-45 ${variant === "dark" ? "border-white/25 group-hover:bg-[var(--teal)]" : "border-[var(--ink)]/15 group-hover:bg-[var(--ink)] group-hover:text-white"}`}>↗</span>
        </div>
        <div className="relative mt-10">
          <h3 className="font-display text-2xl font-normal leading-tight md:text-[1.75rem]">{title}</h3>
          <p className={`mt-3 max-w-md text-[0.95rem] ${variant === "dark" ? "text-white/75" : "text-[var(--muted)]"}`}>{desc}</p>
        </div>
      </Link>
    </Item>
  );
};

export const ServicesBento = () => {
  const { t, r } = useLang();
  return (
    <section id="servicios" className="scroll-mt-24 pb-24 pt-24 md:pb-36 md:pt-32" data-testid="services-section">
      <Container>
        <div className="mb-14 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div className="max-w-3xl">
            <Reveal><Eyebrow>{t.services.eyebrow}</Eyebrow></Reveal>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-[var(--ink)] md:text-5xl"><MaskLines inView lines={[t.services.title]} /></h2>
          </div>
          <Reveal className="max-w-sm text-[var(--muted)]">{t.services.intro}</Reveal>
        </div>
        <Stagger className="grid auto-rows-[minmax(240px,auto)] gap-5 md:grid-cols-12">
          <BentoCard k="translation" n="01" to={r("translation")} className="md:col-span-7 md:row-span-2" variant="light" img="/media/img/a4.jpg" />
          <BentoCard k="certified" n="02" to={r("certified")} className="md:col-span-5" variant="dark" />
          <BentoCard k="interpreting" n="03" to={r("interpreting")} className="md:col-span-5" variant="teal" />
          <BentoCard k="intercultural" n="04" to={r("events")} className="md:col-span-6" variant="sand" />
          <BentoCard k="localisation" n="05" to={r("writing")} className="md:col-span-3" />
          <BentoCard k="proofreading" n="06" to={r("other")} className="md:col-span-3" />
          <BentoCard k="writing" n="07" to={r("writing")} className="md:col-span-12" variant="sand" />
        </Stagger>
        <Reveal className="mt-10"><ArrowLink to={r("services")} data-testid="services-all-link">{t.services.more}</ArrowLink></Reveal>
      </Container>
    </section>
  );
};
